/**
 * ReadinessPanel — the universal user-facing surface for "is my
 * output ready?". Hosted by every consumer node (AssemblyInspectorPanel,
 * TargetTableNode, DestinationNode, PipelineOutputsDrawer) and by the
 * mapping canvas pill slide-over.
 *
 * Layout, top → bottom:
 *
 *   1. Top-line status sentence + count
 *   2. "To fix" list (blockers) — visible by default when present
 *   3. "FYI" list (advisory + info) — collapsed by default
 *   4. Preview pane — rendered by the consumer-specific child
 *      component the host passes via the ``previewSlot`` prop
 *   5. Primary action when ready / secondary preview-only action
 *      when not ready
 *
 * The panel is intentionally dumb: it doesn't fetch, doesn't know how
 * to download, doesn't know about routing. Hosts pass findings +
 * callbacks; the panel renders. This keeps the same component reusable
 * for every consumer and testable in isolation.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as ReadyIcon,
  WarningAmber as AdvisoryIcon,
  Block as BlockerIcon,
} from '@mui/icons-material';

import type {
  ConsumerKind,
  ReadinessFinding,
  ReadinessStatus,
} from '../../../types/readiness';
import { deriveStatus } from '../../../types/readiness';
import ReadinessFindingCard from './ReadinessFindingCard';

interface ReadinessPanelProps {
  consumerKind: ConsumerKind;
  findings: ReadinessFinding[];
  /** Status from the backend; falls back to a client-side derivation. */
  status?: ReadinessStatus;
  /** Optional message (e.g. "No assembly has been run yet."). */
  message?: string;
  /** Slot for the consumer-specific preview pane. */
  previewSlot?: React.ReactNode;
  /** Called when user clicks a primary action on a card. */
  onPrimaryAction?: (finding: ReadinessFinding) => void;
  /** Called when user clicks a card body. */
  onCardClick?: (finding: ReadinessFinding) => void;
  /** Hover hooks for canvas edge emphasis. */
  onHoverEnter?: (finding: ReadinessFinding) => void;
  onHoverLeave?: (finding: ReadinessFinding) => void;
  /** Currently-highlighted finding code (host selection); card border emphasized. */
  highlightedCode?: string;
  /** Optional "ready" action — e.g. Download CSV / Download ODM XML / Run export. */
  readyAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  /** Optional "preview-only" action shown when status=not_ready. */
  previewAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Optional inline-resolution submitter. Threaded through to each
   * card. When present, findings with ``resolution_options`` render
   * the inline form (replacing the navigation button). Host wires
   * this to ``resolvePostMappingFindings`` (workspace inspector) or
   * the pre-mapping equivalent (Generate Mapping dialog).
   */
  onResolve?: (
    request: import('../../../types/readiness').ReadinessResolutionRequest,
  ) => Promise<
    import('./ReadinessFindingCard').ResolveDispatchInfo | void
  >;
  /** Optional lookup-file uploader threaded through to each card's form. */
  onUploadLookup?: (file: File) => Promise<{ alias: string }>;
}

const STATUS_COPY: Record<
  ReadinessStatus,
  { icon: React.ReactElement; line: (n: number) => string }
> = {
  ready: {
    icon: <ReadyIcon color="success" />,
    line: () => 'Ready',
  },
  ready_with_notes: {
    icon: <AdvisoryIcon color="warning" />,
    line: (n) => `Ready — ${n} note${n === 1 ? '' : 's'} to review`,
  },
  not_ready: {
    icon: <BlockerIcon color="error" />,
    line: (n) => `Not ready — ${n} thing${n === 1 ? '' : 's'} to fix`,
  },
};

export function ReadinessPanel({
  consumerKind,
  findings,
  status,
  message,
  previewSlot,
  onPrimaryAction,
  onCardClick,
  onHoverEnter,
  onHoverLeave,
  highlightedCode,
  readyAction,
  previewAction,
  onResolve,
  onUploadLookup,
}: ReadinessPanelProps): React.ReactElement {
  const effectiveStatus: ReadinessStatus = status ?? deriveStatus(findings);
  const blockers = findings.filter((f) => f.severity === 'blocker');
  const fyi = findings.filter((f) => f.severity !== 'blocker');
  const headlineCount =
    effectiveStatus === 'not_ready' ? blockers.length : fyi.length;
  const cfg = STATUS_COPY[effectiveStatus];
  const [fyiOpen, setFyiOpen] = React.useState(false);

  // The card-key uses the finding's stable `target_path + code`
  // so React reconciliation is stable across re-fetches.
  const cardKey = (f: ReadinessFinding) =>
    `${f.code}::${f.target_path}::${JSON.stringify(f.parent_identity ?? {})}`;

  // When fully ready with nothing to fix and no FYI items, the headline is
  // pure ornament — the readiness chip in the consumer's header already
  // says "Ready". Hide it to give the previewSlot more room.
  const showHeadline =
    effectiveStatus !== 'ready' || blockers.length > 0 || fyi.length > 0 || !!message;

  return (
    <Stack spacing={1} sx={{ px: 2, py: 1 }}>
      {/* Top-line status — compact single row that also hosts the FYI toggle
          so we don't burn a second row on "FYI (1)" when the only finding is
          informational. */}
      {showHeadline && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {React.cloneElement(cfg.icon, { sx: { fontSize: 18 } })}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {cfg.line(headlineCount)}
          </Typography>
          {fyi.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{ cursor: 'pointer', ml: 1 }}
              onClick={() => setFyiOpen((v) => !v)}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.3 }}
              >
                FYI ({fyi.length})
              </Typography>
              <IconButton
                size="small"
                aria-label={fyiOpen ? 'Hide FYI' : 'Show FYI'}
                sx={{ p: 0.25 }}
              >
                {fyiOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>
          )}
        </Stack>
      )}

      {message && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
          {message}
        </Alert>
      )}

      {/* "To fix" list */}
      {blockers.length > 0 && (
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            To fix ({blockers.length})
          </Typography>
          <Stack spacing={1}>
            {blockers.map((f) => (
              <ReadinessFindingCard
                key={cardKey(f)}
                finding={f}
                onPrimaryAction={onPrimaryAction}
                onCardClick={onCardClick}
                onHoverEnter={onHoverEnter}
                onHoverLeave={onHoverLeave}
                highlighted={f.code === highlightedCode}
                onResolve={onResolve}
                onUploadLookup={onUploadLookup}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* FYI cards (only when toggle is open) */}
      {fyi.length > 0 && (
        <Collapse in={fyiOpen} unmountOnExit>
          <Stack spacing={1}>
            {fyi.map((f) => (
              <ReadinessFindingCard
                key={cardKey(f)}
                finding={f}
                onPrimaryAction={onPrimaryAction}
                onCardClick={onCardClick}
                onHoverEnter={onHoverEnter}
                onHoverLeave={onHoverLeave}
                highlighted={f.code === highlightedCode}
                onResolve={onResolve}
                onUploadLookup={onUploadLookup}
              />
            ))}
          </Stack>
        </Collapse>
      )}

      {/* Preview slot — consumer-specific child */}
      {previewSlot && <Box>{previewSlot}</Box>}

      {/* Action row */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 1 }}
      >
        {effectiveStatus === 'not_ready' && previewAction && (
          <Button
            variant="outlined"
            size="small"
            onClick={previewAction.onClick}
          >
            {previewAction.label}
          </Button>
        )}
        {readyAction && (
          <Button
            variant="contained"
            size="small"
            color={effectiveStatus === 'ready' ? 'primary' : 'inherit'}
            disabled={
              readyAction.disabled ?? effectiveStatus === 'not_ready'
            }
            onClick={readyAction.onClick}
          >
            {readyAction.label}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export default ReadinessPanel;
